type CacheEnvelope<T> = {
  value: T;
  updatedAt: number;
};

const AGENCY_CACHE_PREFIX = "sendcollab:agency:";

const canUseSessionStorage = () =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const buildCacheKey = (key: string) => `${AGENCY_CACHE_PREFIX}${key}`;

export const agencyCacheKeys = {
  profile: (userId: string) => `profile:${userId}`,
  subscription: (userId: string) => `subscription:${userId}`,
  account: (agencyId: string) => `account:${agencyId}`,
  roster: (agencyId: string) => `roster:${agencyId}`,
};

export const readAgencyCacheEntry = <T,>(key: string): CacheEnvelope<T> | null => {
  if (!canUseSessionStorage()) return null;

  try {
    const raw = window.sessionStorage.getItem(buildCacheKey(key));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<CacheEnvelope<T>>;
    if (parsed && typeof parsed.updatedAt === "number" && "value" in parsed) {
      return parsed as CacheEnvelope<T>;
    }
  } catch {
    return null;
  }

  return null;
};

export const writeAgencyCache = <T,>(key: string, value: T) => {
  if (!canUseSessionStorage()) return;

  try {
    window.sessionStorage.setItem(
      buildCacheKey(key),
      JSON.stringify({ value, updatedAt: Date.now() })
    );
  } catch {
    // Ignore storage quota / private mode failures.
  }
};

export const removeAgencyCacheKey = (key: string) => {
  if (!canUseSessionStorage()) return;

  try {
    window.sessionStorage.removeItem(buildCacheKey(key));
  } catch {
    // Ignore storage failures.
  }
};

export const clearAgencySessionCache = () => {
  if (!canUseSessionStorage()) return;

  try {
    const prefix = buildCacheKey("");
    const keysToRemove: string[] = [];

    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(prefix)) keysToRemove.push(key);
    }

    keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Ignore storage failures.
  }
};

export const shouldRetryAgencyRequest = (
  failureCount: number,
  error: unknown,
  maxRetries = 1
) => {
  if (failureCount >= maxRetries) return false;

  const message = error instanceof Error ? error.message : String(error ?? "");
  const status =
    typeof error === "object" && error !== null && "status" in error
      ? Number((error as { status?: number }).status)
      : undefined;

  if (status && status >= 400 && status < 500) return false;

  return !/permission denied|42501|unauthorized|forbidden/i.test(message);
};